import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Grid,
  Container,
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  Paper
} from '@mui/material';
import { Close } from '@mui/icons-material';
import '../../../styles/featuredGrid.css';
import MissingContent from '../errorMessages/MissingContent';

interface FeaturedGalleryProps {
  images: any[];
  hasVideo: boolean;
  invert: boolean;
  title: string;
  description: string;
  itemGroup?: string;
  application?: string;
  collection?: string;
  useModal?: boolean;
  modalUrl?: string;
  catalogueUrl?: string;
  marginTop?: number;
  marginBottom?: number;
}

function FeaturedGallery({
                           images,
                           hasVideo,
                           invert,
                           title,
                           description,
                           itemGroup,
                           application,
                           collection,
                           useModal = false,
                           modalUrl,
                           catalogueUrl,
                           marginTop,
                           marginBottom
                         }: FeaturedGalleryProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const findImageByPosition = (position: string) => {
    return images.find((img: any) => img.position === position)?.url;
  };

  // Precedence matches the original: modal > external catalogue > internal route
  const isModalTrigger = useModal && !!modalUrl;
  const isExternalLink = !isModalTrigger && !!catalogueUrl;
  const isInternalLink =
    !isModalTrigger && !catalogueUrl && !!(itemGroup && application && collection);

  const internalTo = isInternalLink
    ? `../item_group/${itemGroup}/application/${encodeURIComponent(
      application as string
    )}/collection/${encodeURIComponent(collection as string)}`
    : undefined;

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const showClickableCursor = isModalTrigger || isExternalLink || isInternalLink;

  // Spread this onto the Container/Button so each case renders a real
  // anchor (for right-click/middle-click support) except the modal case,
  // which genuinely isn't a navigation.
  const clickableProps: any = isInternalLink
    ? { component: Link, to: internalTo }
    : isExternalLink
      ? { component: 'a', href: catalogueUrl, target: '_blank', rel: 'noopener noreferrer' }
      : isModalTrigger
        ? { onClick: handleOpenModal }
        : {};

  return (
    <>
      {/* Desktop*/}
      {images === null || images.length === 0 ? (
        <MissingContent sectionName="Featured Collection" />
      ) : (
        <>
          <Container
            maxWidth="lg"
            className="grid-lg"
            sx={{ cursor: showClickableCursor ? 'pointer' : 'default', textDecoration: 'none', color: 'inherit' }}
            {...clickableProps}
          >
            <Grid mt={marginTop || 5} mb={marginBottom || 5} container spacing={2}>
              <Grid id="inner-grid" item xs={6}>
                <Box overflow="hidden" sx={{}}>
                  <img
                    data-product-image="true"
                    style={{
                      width: '100%',
                      height: '80vh',
                      objectFit: 'cover',
                      padding: '1rem',
                    }}
                    src={findImageByPosition('top left')}
                    alt="featured-0"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                  />
                </Box>
                {invert === true ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: '40vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {hasVideo ? (
                      <video
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        autoPlay
                        muted
                        loop
                      >
                        <source src={''} type="video/mp4"></source>
                      </video>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: { xs: '90%', lg: '100%' },
                          height: '100%',
                        }}
                      >
                        <Typography variant="h4">{title}</Typography>
                        <Typography textAlign="center">
                          {description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    overflow="hidden"
                    sx={{
                      marginTop: '15px',
                    }}
                  >
                    <img
                      data-product-image="true"
                      style={{
                        width: '100%',
                        height: '40vh',
                        objectFit: 'cover',
                        paddingRight: '1rem',
                        paddingLeft: '1rem',
                      }}
                      src={findImageByPosition('bottom left')}
                      alt="featured-1"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </Box>
                )}
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  padding: '5px',
                }}
              >
                {invert === true ? (
                  <Box overflow="hidden" height="600px">
                    <img
                      data-product-image="true"
                      style={{
                        width: '100%',
                        height: '40vh',
                        objectFit: 'cover',
                      }}
                      src={findImageByPosition('bottom right')}
                      alt="featured-2"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {hasVideo ? (
                      <video
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        autoPlay
                        muted
                        loop
                      >
                        <source src={''} type="video/mp4"></source>
                      </video>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: { xs: '90%', lg: '100%' },
                          height: '40vh',
                          gap: '19px',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: '10px 12px 10px 12px',
                            background: '#EDEDED',
                          }}
                        >
                          <Typography
                            textTransform={'uppercase'}
                            fontSize={'12px'}
                          >
                            Featured Collection
                          </Typography>
                        </Box>
                        <Typography
                          variant="h1"
                          fontSize={'46px'}
                          fontWeight={'500'}
                          lineHeight={'32px'}
                        >
                          {title}
                        </Typography>
                        <Typography
                          textAlign="center"
                          fontSize={'16px'}
                          fontWeight={'400'}
                          lineHeight={'24px'}
                        >
                          {description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                <Box overflow="hidden" marginTop="15px">
                  <img
                    data-product-image="true"
                    style={{
                      width: '100%',
                      height: '80vh',
                      marginTop: '3px',
                      objectFit: 'cover',
                    }}
                    src={findImageByPosition('bottom right')}
                    alt={`featured-2`}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>

          {/* Mobile View */}
          <Grid
            className="grid-sm"
            container
            flexDirection="column"
            display={'flex'}
            justifyContent={'center'}
            alignItems="center"
            mt={marginTop || 5}
            mb={marginBottom || 5}
            paddingX="16px"
          >
            <Box
              sx={{
                maxWidth: '100%',
              }}
            >
              <Box>
                <img
                  data-product-image="true"
                  style={{
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover',
                  }}
                  src={findImageByPosition('bottom right')}
                  alt="featured-2"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  width: '100%',
                  height: 'auto',
                  pt: '1rem',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: '10px 12px 10px 12px',
                    background: '#EDEDED',
                  }}
                >
                  <Typography textTransform={'uppercase'} fontSize={'12px'}>
                    Featured Collection
                  </Typography>
                </Box>
                <Typography
                  mt={3}
                  variant="h5"
                  fontSize={'40px'}
                  fontWeight={'400'}
                >
                  {title}
                </Typography>
                <Typography
                  fontSize={'16px'}
                  mt={2}
                  sx={{
                    width: '100%',
                  }}
                >
                  {description}
                </Typography>
                <Button
                  sx={{
                    mt: 3,
                    mb: 5,
                    borderRadius: '0',
                    padding: '1rem 3rem',
                    backgroundColor: '#fff',
                  }}
                  variant="outlined"
                  color="primary"
                  {...clickableProps}
                >
                  {useModal ? 'View Collection' : catalogueUrl ? 'View Catalogue' : 'Explore The Collection'}
                </Button>
              </Box>
            </Box>
          </Grid>
        </>
      )}

      {/* Modal for International Brands */}
      {useModal && (
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="featured-collection-modal"
          aria-describedby="featured-collection-viewer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Paper
            sx={{
              position: 'relative',
              width: '95vw',
              height: '90vh',
              maxWidth: '1200px',
              maxHeight: '800px',
              outline: 'none',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 24,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                },
              }}
            >
              <Close />
            </IconButton>

            {modalUrl && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  paddingTop: 'max(60%, 326px)',
                }}
              >
                <iframe
                  allow="clipboard-write"
                  sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox allow-forms"
                  allowFullScreen={true}
                  style={{
                    position: 'absolute',
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                  }}
                  src={modalUrl}
                  title={`Featured Collection - ${title}`}
                />
              </Box>
            )}
          </Paper>
        </Modal>
      )}
    </>
  );
}

export default FeaturedGallery;